import { CheckCircle, AlertTriangle, XCircle, Clock, MinusCircle } from 'lucide-react';
import { MODULE_DISPLAY_NAMES, type ModuleRun, type ModuleStatus } from '@/types/forensics';

const STATUS_CONFIG: Record<ModuleStatus, { icon: React.ReactNode; className: string }> = {
  pending: { icon: <Clock className="h-3.5 w-3.5" />, className: 'text-muted-foreground' },
  running: { icon: <Clock className="h-3.5 w-3.5 animate-spin" />, className: 'text-blue-600' },
  success: { icon: <CheckCircle className="h-3.5 w-3.5" />, className: 'text-green-600' },
  warning: { icon: <AlertTriangle className="h-3.5 w-3.5" />, className: 'text-orange-500' },
  failed: { icon: <XCircle className="h-3.5 w-3.5" />, className: 'text-red-500' },
  skipped: { icon: <MinusCircle className="h-3.5 w-3.5" />, className: 'text-gray-400' },
};

interface TechnicalLogsTabProps {
  moduleRuns: ModuleRun[];
}

export default function TechnicalLogsTab({ moduleRuns }: TechnicalLogsTabProps) {
  if (moduleRuns.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No module execution data available.</p>;
  }

  return (
    <div className="space-y-1">
      <h4 className="text-sm font-medium mb-2">Module Execution Summary</h4>
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left px-3 py-2 font-medium">Module</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Runtime</th>
              <th className="text-left px-3 py-2 font-medium">Version</th>
            </tr>
          </thead>
          <tbody>
            {moduleRuns.map((run) => {
              const config = STATUS_CONFIG[run.status];
              return (
                <tr key={run.id} className="border-t">
                  <td className="px-3 py-2">{MODULE_DISPLAY_NAMES[run.moduleName] || run.moduleName}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 ${config.className}`}>
                      {config.icon}
                      {run.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-muted-foreground">
                    {run.runtimeMs != null ? `${(run.runtimeMs / 1000).toFixed(1)}s` : '-'}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{run.version || '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Warnings/errors */}
      {moduleRuns
        .filter((r) => r.warningMessage || r.errorMessage)
        .map((run) => (
          <div
            key={run.id}
            className={`p-2 rounded text-xs mt-2 ${
              run.errorMessage
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-orange-50 border border-orange-200 text-orange-700'
            }`}
          >
            <span className="font-medium">{MODULE_DISPLAY_NAMES[run.moduleName]}:</span> {run.errorMessage || run.warningMessage}
          </div>
        ))}
    </div>
  );
}
